import { useState, useEffect } from 'react';
import EnterpriseDashboard from '@/components/EnterpriseDashboard';
import CommandCenter from './components/command-center/CommandCenter';
import AskAgentomy from './components/AskAgentomy';
import LoginPage from './components/LoginPage';
import { DemoProvider } from './components/DemoContext';
import { isAuthenticated, probeAuthMode, logout } from './lib/api';
import './globals.css';

type AuthMode = 'unknown' | 'open' | 'auth' | 'offline';

function App() {
  // Stage A: both builds land on the Command Center -- the fully-wired governance surface.
  // The legacy Enterprise Dashboard has stale endpoint wiring (404 audit/trustchain feeds,
  // /health shape drift) that misrepresents a healthy system, so its nav button is hidden
  // until the wiring is rehabilitated (Stage B). The enterprise dashboard module is still
  // aliased to a stub in the public build (vite.config.ts), so no proprietary code ships.
  const SHOW_LEGACY_DASHBOARD = false;
  const [view, setView] = useState<'dashboard' | 'command-center'>('command-center');
  const [authMode, setAuthMode] = useState<AuthMode>('unknown');
  const [authed, setAuthed] = useState(false);

  // On mount: probe whether auth is required, then check sessionStorage
  useEffect(() => {
    probeAuthMode().then((mode) => {
      setAuthMode(mode);
      if (mode === 'open' || mode === 'offline') {
        // No auth required (dev mode or API offline) -- proceed without login
        setAuthed(true);
      } else {
        // auth mode: check if we already have a key from a prior page load
        setAuthed(isAuthenticated());
      }
    });
  }, []);

  const handleAuthenticated = () => setAuthed(true);

  const handleLogout = () => {
    logout(); // clears sessionStorage and redirects to /
  };

  // While probing, show a minimal dark screen -- avoids flash of login on dev
  if (authMode === 'unknown') {
    return (
      <div style={{ minHeight: '100vh', background: '#1C1917' }} />
    );
  }

  // Auth required and not yet authenticated
  if (authMode === 'auth' && !authed) {
    return <LoginPage onAuthenticated={handleAuthenticated} />;
  }

  // Authenticated (or open mode)
  return (
    <DemoProvider>
    <div className="App dark">
      {/* When SHOW_LEGACY_DASHBOARD is true, render the full top-nav with Dashboard + Command Center
          switching tabs. When it's false (Stage A only), there's nothing to switch -- so we skip the
          top-bar entirely and float just the Sign-out button absolute top-right. This removes the
          dark bar that was overlapping the inner Sidebar 'Agentomy' logo. */}
      {SHOW_LEGACY_DASHBOARD ? (
        <nav
          aria-label="Main navigation"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            display: 'flex', gap: '2px', padding: '4px 8px',
            background: '#0a0f1e', borderBottom: '1px solid #1a1f2e',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setView('dashboard')}
            aria-current={view === 'dashboard' ? 'page' : undefined}
            aria-pressed={view === 'dashboard'}
            style={{
              padding: '6px 16px', fontSize: '13px', cursor: 'pointer',
              background: view === 'dashboard' ? '#10b981' : '#1a1f2e',
              color: view === 'dashboard' ? '#0a0f1e' : '#94a3b8',
              border: 'none', borderRadius: '4px', fontWeight: 600
            }}
          >
            Dashboard
          </button>
          <button
            onClick={() => setView('command-center')}
            aria-current={view === 'command-center' ? 'page' : undefined}
            aria-pressed={view === 'command-center'}
            style={{
              padding: '6px 16px', fontSize: '13px', cursor: 'pointer',
              background: view === 'command-center' ? '#10b981' : '#1a1f2e',
              color: view === 'command-center' ? '#0a0f1e' : '#94a3b8',
              border: 'none', borderRadius: '4px', fontWeight: 600
            }}
          >
            Command Center
          </button>
          {authMode === 'auth' && (
            <button
              onClick={handleLogout}
              style={{
                marginLeft: 'auto',
                padding: '5px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #1e293b',
                borderRadius: '4px',
                fontWeight: 500,
              }}
            >
              Sign out
            </button>
          )}
        </nav>
      ) : (
        authMode === 'auth' && (
          <button
            onClick={handleLogout}
            style={{
              position: 'fixed', top: '12px', right: '16px', zIndex: 9999,
              padding: '5px 12px',
              fontSize: '12px',
              cursor: 'pointer',
              background: 'rgba(33, 30, 28, 0.85)',
              color: '#A8A29E',
              border: '1px solid #3A352F',
              borderRadius: '6px',
              fontWeight: 500,
              transition: 'color 0.15s, border-color 0.15s',
              backdropFilter: 'blur(8px)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = '#F87171';
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#F87171';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = '#A8A29E';
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#3A352F';
            }}
          >
            Sign out
          </button>
        )
      )}
      {/* Ambient background gradient - very slow 30s cycle */}
      <div
        className="app-ambient-bg"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          opacity: 0.03,
          background: 'radial-gradient(ellipse at 30% 50%, #C2714F, transparent 70%), radial-gradient(ellipse at 70% 50%, #D4825F, transparent 70%)',
          animation: 'ambientShift 30s ease-in-out infinite alternate',
        }}
      />
      <style>{`
        @keyframes ambientShift {
          0% { opacity: 0.03; transform: scale(1) translate(0, 0); }
          100% { opacity: 0.05; transform: scale(1.05) translate(2%, -1%); }
        }
        @keyframes viewCrossfade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .app-ambient-bg { animation: none !important; }
          .view-transition { animation: none !important; }
        }
      `}</style>
      <div style={{ paddingTop: SHOW_LEGACY_DASHBOARD ? '36px' : '0', position: 'relative', zIndex: 1 }}>
        <div key={view} className="view-transition" style={{ animation: 'viewCrossfade 0.3s ease' }}>
          {view === 'dashboard' ? <EnterpriseDashboard /> : <CommandCenter />}
        </div>
      </div>
      <AskAgentomy />
    </div>
    </DemoProvider>
  );
}

export default App;
